/**
 * HITLFrame — hosts the WPD editor in an iframe and listens for postMessage
 * events from hitl_editor/index.html.
 *
 * Layout:
 *   ┌─ Sidebar ────────────────────────┐  ┌─ WPD iframe ───────────────┐
 *   │ Chart info                       │  │ Banner (our HTML)          │
 *   │ Trigger reason + details         │  │ WPD canvas (unmodified)    │
 *   │ Quick Approve button             │  │ Submit bar (our HTML)      │
 *   └──────────────────────────────────┘  └────────────────────────────┘
 *
 * The "Submit Corrections" and "Approve" buttons inside the iframe handle
 * the API calls directly (see hitl_editor/index.html).  The parent
 * receives postMessage events and refreshes the chart list accordingly.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { HITLInitResponse } from "../../api/client";
import { useJobStore } from "../../stores/jobStore";

interface HITLFrameProps {
  jobId: string;
  init: HITLInitResponse;
  onClose: () => void;
}

type FrameEvent =
  | { event: "APPROVED"; detail: { chartId: string; outcome: string } }
  | { event: "CORRECTIONS_SUBMITTED"; detail: { chartId: string; correctionDelta: number; calibrationChanged: boolean } }
  | { event: "FLAGGED"; detail: { chartId: string; reason: string } };

export function HITLFrame({ jobId, init, onClose }: HITLFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeReady, setIframeReady] = useState(false);
  const { selectJob } = useJobStore();

  // ── Listen for postMessage from WPD wrapper ────────────────────────────
  const handleMessage = useCallback(
    (evt: MessageEvent) => {
      if (!evt.data || evt.data.source !== "rdp-hitl") return;

      const msg = evt.data as FrameEvent;

      switch (msg.event) {
        case "APPROVED":
        case "CORRECTIONS_SUBMITTED":
        case "FLAGGED":
          // Refresh the full job (chart list will update via WS, but trigger reload too)
          selectJob(jobId).then(onClose);
          break;
      }
    },
    [jobId, onClose, selectJob]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  // ── Confidence colour ──────────────────────────────────────────────────
  const conf = init.pipeline_confidence;
  const confColor =
    conf == null ? "text-gray-400" :
    conf >= 0.85 ? "text-green-600" :
    conf >= 0.65 ? "text-amber-600" :
                   "text-red-600";

  const confText = conf != null ? `${(conf * 100).toFixed(1)}%` : "—";

  const isGateFailure = init.trigger_reason.startsWith("GATE_");

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-900">
      {/* ── Header bar ───────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 h-10 bg-gray-800 text-white text-sm flex-shrink-0">
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
          aria-label="Close HITL editor"
        >
          ✕
        </button>
        <span className="text-gray-400">|</span>
        <span className="font-medium">
          HITL Review — Page {init.page_num + 1} · Chart {init.chart_idx + 1}
        </span>
        <span className="text-gray-400">|</span>
        <span className={`font-mono ${confColor}`}>conf {confText}</span>
        <span className="text-gray-400">|</span>
        <span
          className={`px-2 py-0.5 rounded text-xs font-semibold ${
            isGateFailure
              ? "bg-red-900 text-red-200"
              : "bg-amber-900 text-amber-200"
          }`}
        >
          {init.trigger_reason}
        </span>
        {init.chart_type && (
          <>
            <span className="text-gray-400">|</span>
            <span className="text-gray-300 text-xs">{init.chart_type}</span>
          </>
        )}
        <span className="ml-auto text-xs text-gray-500">
          Session {init.hitl_session_id.slice(0, 8)}…
        </span>
      </div>

      {/* ── Main content: sidebar + iframe ───────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-64 bg-gray-800 flex-shrink-0 overflow-y-auto p-4 space-y-4 border-r border-gray-700">
          {/* Trigger details */}
          <section>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Trigger Details
            </h3>
            {init.trigger_details ? (
              <pre className="text-xs text-gray-300 bg-gray-900 rounded p-2 overflow-auto max-h-40">
                {JSON.stringify(init.trigger_details, null, 2)}
              </pre>
            ) : (
              <p className="text-xs text-gray-500">No details available.</p>
            )}
          </section>

          {/* Instructions */}
          <section>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Instructions
            </h3>
            <ol className="text-xs text-gray-300 space-y-1.5 list-decimal list-inside">
              <li>Verify the axis calibration diamonds are on the correct tick marks.</li>
              <li>Check that curve families match the physical chart labels.</li>
              <li>Drag misplaced points or add missing ones.</li>
              <li>
                {isGateFailure ? (
                  <>Fix the failing gate check: <strong className="text-red-400">{init.trigger_reason.replace("GATE_", "")}</strong></>
                ) : (
                  "Review and adjust any low-confidence points (shown in red)."
                )}
              </li>
              <li>Click <strong className="text-blue-300">Submit Corrections</strong> in the blue bar below the canvas, or <strong className="text-green-300">Approve</strong> if no changes are needed.</li>
            </ol>
          </section>

          {/* WPD shortcut reference */}
          <section>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              WPD Shortcuts
            </h3>
            <table className="text-xs text-gray-300 w-full">
              <tbody className="divide-y divide-gray-700">
                {[
                  ["Shift+Click", "Add point"],
                  ["Drag point", "Reposition"],
                  ["Right-click", "Assign family"],
                  ["Box-select + Del", "Bulk delete"],
                  ["Ctrl+Z / Ctrl+Y", "Undo / Redo"],
                  ["Scroll", "Zoom"],
                  ["Space+Drag", "Pan"],
                ].map(([key, desc]) => (
                  <tr key={key}>
                    <td className="py-0.5 pr-2 font-mono text-blue-300">{key}</td>
                    <td className="py-0.5 text-gray-400">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </aside>

        {/* WPD iframe */}
        <div className="flex-1 relative">
          {!iframeReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-10">
              <div className="text-center space-y-3">
                <div className="w-8 h-8 border-2 border-gray-600 border-t-indigo-400 rounded-full animate-spin mx-auto" />
                <p className="text-gray-400 text-sm">Loading WebPlotDigitizer…</p>
              </div>
            </div>
          )}
          <iframe
            ref={iframeRef}
            src={init.wpd_editor_url}
            className="w-full h-full border-0"
            title="WebPlotDigitizer HITL Editor"
            onLoad={() => setIframeReady(true)}
            // sandbox: allow-scripts needed for WPD to run; allow-same-origin
            // needed so WPD's fetch calls to /api/files and /download/* work.
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        </div>
      </div>
    </div>
  );
}
